import React, { useState, useEffect } from 'react';
import { Paper, Typography, Divider, Box, Button, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, CircularProgress, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import AddIcon from '@mui/icons-material/Add';

const SameAllocationOrders = ({ orders, assetOptions, handleOrderFieldChange, setOrders, investors: investorsProp, loading = false, getInitials, error, errorMessage, onAllocationError, onCanProceedChange, onInvestorsChange }) => {

 
  const [investors, setInvestors] = useState(investorsProp);
  const [newAsset, setNewAsset] = React.useState(null);
  const [newAllocation, setNewAllocation] = React.useState('');
  const [newLimitPrice, setNewLimitPrice] = React.useState('');
  const [hasDeletedAsset, setHasDeletedAsset] = useState(false);
  const [deletedAssets, setDeletedAssets] = useState([]);
  const [deletedAssetsMap, setDeletedAssetsMap] = useState({}); // Track most recent deletion per asset
  const [currentOrderType, setCurrentOrderType] = useState(orders[0]?.orderType || '');
  const [firstAssetRemoved, setFirstAssetRemoved] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);

  // Sync local state if prop changes
  useEffect(() => {
    setInvestors(investorsProp);
  }, [investorsProp]);

  // Input validation functions
  const validateNumericInput = (value) => {
    // Allow empty string, numbers, and decimal points
    return /^$|^[0-9]*\.?[0-9]*$/.test(value);
  };

  const validateAllocationInput = (value) => {
    // Allow empty string, numbers, and decimal points
    // Don't restrict to 100 during typing - let user type any number
    return /^$|^[0-9]*\.?[0-9]*$/.test(value);
  };

  const handleAllocationChange = (idx, value) => {
    if (validateAllocationInput(value)) {
      handleOrderFieldChange(idx, 'allocation', value);
    }
  };

  const handleLimitPriceChange = (idx, value) => {
    if (validateNumericInput(value)) {
      handleOrderFieldChange(idx, 'limitPrice', value);
    }
  };


  // Sync current order type when orders change
  useEffect(() => {
    if (orders.length > 0 && orders[0]?.orderType) {
      setCurrentOrderType(orders[0].orderType);
    }
    else if (orders.length === 0) {
      setCurrentOrderType('');
      setDeletedAssets([]); // Clear deleted assets when all orders are deleted
      setDeletedAssetsMap({}); // Clear deleted assets map when all orders are deleted
      setHasDeletedAsset(false); // Reset deleted asset flag
    }
    
    // Update delete disabled state based on orders length
    setIsDeleteDisabled(orders.length <= 1);
    
  }, [orders]);

  // Reset component state when orders are reset to initial state
  useEffect(() => {
    if (orders.length === 1 && !orders[0].asset && !orders[0].allocation) {
      // Reset all internal state when orders are reset to initial state
      setCurrentOrderType('');
      setDeletedAssets([]);
      setDeletedAssetsMap({});
      setHasDeletedAsset(false);
      setFirstAssetRemoved(false);
      setIsDeleteDisabled(true);
      setNewAsset(null);
      setNewAllocation('');
      setNewLimitPrice('');
    }
  }, [orders]);

  

  // Reset firstAssetRemoved when assets are added back
  useEffect(() => {
    if (orders.length > 0 && firstAssetRemoved) {
      setFirstAssetRemoved(false);
    }
  }, [orders.length, firstAssetRemoved]);

  // Remove handler: only remove if more than one investor
  const handleRemoveInvestor = (index) => {
    if (investors.length > 1) {
      const updatedInvestors = investors.filter((_, i) => i !== index);
      setInvestors(updatedInvestors);
      // Notify parent of the change
      if (onInvestorsChange) {
        onInvestorsChange(updatedInvestors);
      }
    }
  };

  // Remove asset handler
  const handleRemoveAsset = (idx) => {
    // Check if this is the last order - if so, don't allow deletion
    if (orders.length <= 1) {
      setIsDeleteDisabled(true);
      return;
    }

    const assetToDelete = orders[idx];

    setOrders(prev => {
      const filteredOrders = prev.filter((_, i) => i !== idx);
      // Update delete disabled state based on remaining orders
      setIsDeleteDisabled(filteredOrders.length <= 1);
      return filteredOrders;
    });

    // This is used to show the reset dialog
    setHasDeletedAsset(true);

    // Store deleted asset for potential restoration
    // Note: Deleted assets are preserved when switching between Market and Limit order types
    setDeletedAssets(prev => [...prev, assetToDelete]);

    // Track the most recent deletion for each asset name
    // Only overwrite if the new deletion has a valid allocation, otherwise keep the previous one
    setDeletedAssetsMap(prev => {
      const currentDeletedAsset = prev[assetToDelete.asset];
      const hasValidAllocation = assetToDelete.allocation !== '' && assetToDelete.allocation !== null && assetToDelete.allocation !== undefined && !isNaN(Number(assetToDelete.allocation));
      
      // If current deletion has valid allocation, use it
      if (hasValidAllocation) {
        return {
          ...prev,
          [assetToDelete.asset]: assetToDelete
        };
      }
      
      // If current deletion has empty allocation but previous deletion had valid allocation, keep the previous one
      if (currentDeletedAsset && currentDeletedAsset.allocation !== '' && currentDeletedAsset.allocation !== null && currentDeletedAsset.allocation !== undefined && !isNaN(Number(currentDeletedAsset.allocation))) {
        return prev; // Keep the previous deletion with valid allocation
      }
      
      // Otherwise, use the current deletion (even if empty allocation)
      return {
        ...prev,
        [assetToDelete.asset]: assetToDelete
      };
    });

    // If removing the first asset (index 0), disable add asset button
    if (idx === 0) {
      setFirstAssetRemoved(true);
    }
  };


  

    // Add new asset handler
  const handleAddAsset = () => {
    // Check if we have available assets to add
    const usedAssets = orders.map(order => order.asset).filter(asset => asset && asset.trim() !== '');
    const availableAssets = assetOptions.filter(option => !usedAssets.includes(option.value));
    
    if (availableAssets.length === 0) {
      return; // No available assets to add
    }
    
    const newOrder = {
      orderType: currentOrderType,
      asset: '',
      allocation: '',
      limitPrice: currentOrderType === 'LMT' ? '' : '',
      limitPriceFocused: currentOrderType === 'LMT', // Focus on limit price if it's a Limit order
    };
    setOrders(prev => [...prev, newOrder]);
  };

  // Reset handler - restore all deleted assets
  const handleReset = () => {
    if (Object.keys(deletedAssetsMap).length === 0) {
      setHasDeletedAsset(false);
      return;
    }

    // Only restore if user explicitly wants to (when allocation is not 100% and there are available assets)
    const allocationSum = orders.length === 0 ? 100 : 
      orders.every(order => !order.allocation || order.allocation === '') ? 100 : 
      orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
    
    const hasAvailableAssets = assetOptions.length > orders.length;
    
    // Only restore if allocation is not 100% and there are available assets
    if (allocationSum === 100 || !hasAvailableAssets) {
      return;
    }

    // Restore deleted assets to the orders, but only if they don't already exist
    // Also prevent duplicate assets with same name but different allocations
    // Only restore assets that have both asset and allocation filled
    setOrders(prev => {
      // Get current asset names to avoid duplicates
      const currentAssetNames = prev
        .map(order => order.asset)
        .filter(asset => asset && asset.trim() !== '');
      
      // Use the most recent deletion for each asset (from deletedAssetsMap)
      // Only restore assets that don't already exist in current orders and have valid values
      const assetsToRestore = Object.values(deletedAssetsMap).filter(deletedAsset => {
        const assetExists = currentAssetNames.includes(deletedAsset.asset);
        const hasValidAsset = deletedAsset.asset && deletedAsset.asset.trim() !== '';
        const hasValidAllocation = deletedAsset.allocation !== '' && deletedAsset.allocation !== null && deletedAsset.allocation !== undefined && !isNaN(Number(deletedAsset.allocation));
        
        return !assetExists && hasValidAsset && hasValidAllocation;
      });
      
      if (assetsToRestore.length > 0) {
        // Preserve the current order type for restored assets
        const restoredAssets = assetsToRestore.map(asset => ({
          ...asset,
          orderType: currentOrderType,
          limitPrice: currentOrderType === 'MKT' ? '' : asset.limitPrice,
          limitPriceFocused: false
        }));
        return [...prev, ...restoredAssets];
      }
      
      return prev;
    });
    
    setDeletedAssets([]);
    setDeletedAssetsMap({});
    setHasDeletedAsset(false);
    setFirstAssetRemoved(false); // Reset the first asset removed flag when restoring
  };

  const allocationSum = orders.length === 0 ? 100 : 
    orders.every(order => !order.allocation || order.allocation === '') ? 100 : 
    orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
  
  // Check if there are available assets to add (not already used in orders)
  const usedAssets = orders.map(order => order.asset).filter(asset => asset && asset.trim() !== '');
  const availableAssets = assetOptions.filter(option => !usedAssets.includes(option.value));
  const hasAvailableAssets = availableAssets.length > 0;
  
  // Check for duplicate assets with same name but different allocations
  const hasDuplicateAssets = () => {
    const assetCounts = {};
    orders.forEach(order => {
      if (order.asset && order.asset.trim() !== '') {
        assetCounts[order.asset] = (assetCounts[order.asset] || 0) + 1;
      }
    });
    return Object.values(assetCounts).some(count => count > 1);
  };
  
  // Check if current orders have valid assets and allocations filled
  const hasValidCurrentOrders = orders.length > 0 && orders.every(order => 
    order.asset && order.asset.trim() !== '' && 
    order.allocation !== '' && order.allocation !== null && order.allocation !== undefined && 
    !isNaN(Number(order.allocation))
  );
  
  // Only allow adding assets if current orders are valid AND there are available assets AND first asset wasn't removed AND no duplicates
  const canAddAsset = hasAvailableAssets && !firstAssetRemoved && hasValidCurrentOrders && !hasDuplicateAssets();
  const isMarket = currentOrderType === 'MKT';
  const isLimit = currentOrderType === 'LMT';
  const allLimitPricesFilled = isLimit
    ? orders.every(order => order.limitPrice && order.limitPrice.trim() !== '' && !isNaN(Number(order.limitPrice)) && Number(order.limitPrice) > 0)
    : true;
  
  // Check if orders have valid assets and allocations
  const hasValidOrders = orders.length > 0 && orders.every(order => 
    order.asset && order.asset.trim() !== '' && 
    order.allocation !== '' && order.allocation !== null && order.allocation !== undefined && 
    !isNaN(Number(order.allocation))
  );
  
  const allocationError = orders.length === 0 ? false : allocationSum !== 100;
  // Allow orders to proceed when allocation is 100% AND orders are valid AND order type is selected, Limit orders need all limit prices filled
  const canProceed = !allocationError && hasValidOrders && currentOrderType && (isMarket || (isLimit && allLimitPricesFilled));

  // Notify parent of allocation error and canProceed
  React.useEffect(() => {
    if (onAllocationError) onAllocationError(allocationError);
    if (typeof onCanProceedChange === 'function') onCanProceedChange(canProceed);
  }, [allocationError, canProceed, onAllocationError, onCanProceedChange]);

  // New: Check for single asset at 100 and another with allocation > 0
  function getSingleAsset100Error() {
    const fullAllocOrder = orders.find(order => Number(order.allocation) === 100);
    if (fullAllocOrder) {
      // If any other order has allocation > 0
      const otherWithAlloc = orders.some(order => order !== fullAllocOrder && Number(order.allocation) > 0);
      if (otherWithAlloc) {
        return `Reduce ${fullAllocOrder.asset || 'asset'} allocation`;
      }
    }
    return '';
  }


  return (
    <>
      <Paper sx={{ width: 570, p: 2, borderRadius: 2, boxShadow: 0, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
          Investor List
        </Typography>
        <Divider sx={{ my: 1 }} />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ 
            maxHeight: investors.length > 3 ? 522 : 'auto', 
            overflowY: investors.length > 3 ? 'auto' : 'visible', 
            pr: 1 
          }}>
            {investors.map((inv, index) => {
              const investorName = inv?.userRole?.user?.userInfo?.firstName || "-";
             
              return (
                <Box
                  key={`investor-${index}`}
                  sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between', width: '100%' }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: '#e0e3e7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      color: '#757575',
                      mr: 2,
                      fontSize: 16,
                      textAlign: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(investorName)}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '16px', color: '#212121' }}>
                    {investorName}
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  size="small"
                  sx={{ color: '#1976d2', textTransform: 'none', fontWeight: 500, fontSize: 16, minWidth: 0, p: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleRemoveInvestor(index)}
                  disabled={investors.length <= 1}
                >
                  Remove
                </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
      <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: 0, border: error ? '2px solid red' : '1px solid #e0e0e0', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center'}}>
          <Typography variant="subtitle2" sx={{ color: '#666' }}>Order List</Typography>
          
          {(error && errorMessage) || getSingleAsset100Error() ? (
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#f44336', ml: 1 }}>
              <ErrorOutlineIcon sx={{ fontSize: 20, mr: 0.5 }} />
              <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
                {getSingleAsset100Error() || errorMessage}
              </Typography>
            </Box>
          ) : null}
          
        </Box>
        <Divider sx={{my: 1}} />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Render a set of inputs for each order */}
            <Box sx={{ my: 2, width: '100%' }}>
              <FormControl fullWidth size="large">
                <InputLabel id="order-type-label">Order Type</InputLabel>
                <Select
                  labelId="order-type-label"
                  value={currentOrderType}
                  label="Order Type"
                  onChange={e => {
                    const newOrderType = e.target.value;
                    setCurrentOrderType(newOrderType);
                    
                    // Update order type for existing orders without resetting deleted assets state
                    // This preserves the current order structure when switching between Market and Limit
                    if (orders.length > 0) {
                      setOrders(prev => prev.map(order => ({
                        ...order,
                        orderType: newOrderType,
                        // Keep existing asset and allocation, only reset limitPrice if switching to Market
                        limitPrice: newOrderType === 'MKT' ? '' : order.limitPrice,
                        limitPriceFocused: newOrderType === 'LMT' // Focus on limit price if switching to Limit
                      })));
                    }
                    // Don't automatically restore deleted assets when switching order types
                    // Let user manually use the Reset Asset button if they want to restore
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-select': {
                      color: '#666',
                      padding: '12px 14px',
                      fontSize: '16px'
                    }
                  }}
                >
                  <MenuItem value={"MKT"}>Market</MenuItem>
                  <MenuItem value={"LMT"}>Limit</MenuItem>
                  {/* <MenuItem value={"TWAP"}>TWAP</MenuItem> */}
                </Select>
              </FormControl>
            </Box>
            {currentOrderType && currentOrderType !== '' && (
              <>
                {orders.length > 0 && orders.map((order, idx) => (
                  
                  <Box key={`order-${idx}-${order.asset || 'empty'}`} sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%', gap: 2, border: '1.5px solid #1976d2', borderRadius: 2, p: 1, background: '#fff' }}>
                    <FormControl sx={{ minWidth: 160 }}>
                      <InputLabel>Asset</InputLabel>
                      <Select
                        value={order.asset}
                        label="Asset"
                        onChange={e => handleOrderFieldChange(idx, 'asset', e.target.value)}
                      >
                        {assetOptions
                          .filter(option => {
                            // Show all options for the current order (to allow changing selection)
                            // But filter out assets that are already selected in other orders
                            const isCurrentOrder = order.asset === option.value;
                            const isUsedInOtherOrders = orders.some((otherOrder, otherIdx) => 
                              otherIdx !== idx && otherOrder.asset === option.value && otherOrder.asset.trim() !== ''
                            );
                            return isCurrentOrder || !isUsedInOtherOrders;
                          })
                          .map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <TextField
                      variant="outlined"
                      color="primary"
                      size="medium"
                      label="Allocation"
                      value={order.allocation}
                      onChange={e => handleAllocationChange(idx, e.target.value)}
                      sx={{ minWidth: 120, background: '#fff' }}
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{
                        endAdornment: order.allocation ? <Typography sx={{ fontWeight: 600, color: '#1976d2', ml: 1 }}>%</Typography> : null
                      }}
                    />
                    {currentOrderType === 'LMT' && (
                      <TextField
                        variant="outlined"
                        color="primary"
                        size="medium"
                        label="Limit Price"
                        value={order.limitPrice}
                        onChange={e => handleLimitPriceChange(idx, e.target.value)}
                        sx={{ minWidth: 140, background: '#fff' }}
                        inputProps={{ min: 0, step: 'any' }}
                        autoFocus={order.limitPriceFocused}
                        onFocus={() => handleOrderFieldChange(idx, 'limitPriceFocused', true)}
                        onBlur={() => handleOrderFieldChange(idx, 'limitPriceFocused', false)}
                        InputProps={{
                          startAdornment: order.limitPrice ? <Typography sx={{ fontWeight: 'bold', color: '#000000', mr: 1 }}>$</Typography> : null
                        }}
                      />
                    )}
                    <DeleteOutlineIcon
                      sx={{ 
                        ml: 2, 
                        cursor: isDeleteDisabled ? 'not-allowed' : 'pointer', 
                        color: isDeleteDisabled ? '#ccc' : '#d32f2f',
                        opacity: isDeleteDisabled ? 0.5 : 1
                      }}
                      onClick={() => !isDeleteDisabled && handleRemoveAsset(idx)}
                    />
                  </Box>
                ))}
              </>
            )}
          </>
        )}
        
        {/* Add Order and Reset Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddAsset}
            disabled={!canAddAsset}
            sx={{
              border: '1px solid #1976d2',
              color: '#1976d2',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 16,
              borderRadius: 2,
              px: 3,
              py: 1,
              '&:hover': {
                border: '1px solid #1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              },
              '&:disabled': {
                border: '1px solid #ccc',
                color: '#ccc'
              }
            }}
          >
            Add Asset
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasDeletedAsset || allocationSum === 100 || !hasAvailableAssets || Object.keys(deletedAssetsMap).length === 0 || !hasValidCurrentOrders}
            sx={{
              border: '1px solid #1976d2',
              color: '#1976d2',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 16,
              borderRadius: 2,
              px: 3,
              py: 1,
              '&:hover': {
             
                border: '1px solid #1976d2',
                backgroundColor: 'rgba(102, 102, 102, 0.04)'
              },
              '&:disabled': {
                border: '1px solid #ccc',
                color: '#ccc',
                cursor: 'not-allowed'
              }
            }}
          >
            Reset Asset
          </Button>
        </Box>
        
        {currentOrderType && (
          <Typography color={allocationError ? 'error' : 'primary'} sx={{ mt: 2 }}>
            Total: {allocationSum}%
          </Typography>
        )}
      </Paper>
    </>
  );
};

export default SameAllocationOrders; 